import GetAppIcon from '@mui/icons-material/GetApp';
import PageviewIcon from '@mui/icons-material/Pageview';
import { Box, Chip, IconButton, Paper, Stack, styled, TableBody, TableCell, TableContainer, Table, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { GridActionsCellItem, GridRowParams } from "@mui/x-data-grid-pro";
import { useStatePtr } from '@pure-ptr/react';
import { Parser } from 'json2csv';
import React, { useEffect, useMemo } from 'react';
import { injectIntl } from "react-intl";
import { useDispatch } from 'react-redux';
import { Switch, TableLevelAction } from '../../../../common';
import { HoverColumn, pageStore, renderHoverLink, renderProcessTypeHoverLink, SortOptions, useGlobalStore, XClientDataGrid } from '../../../../common/XDataGrid';
import { resetClaimLineItem, setAdjustment } from '../../../../store/actions/claimAnalysis';
import { ClaimPaymentDetails } from '../../../../store/reducers/claimAnalysis';
import {
    asDollars,
    createCurrencyDataField, createFloatingPoint, createNumberDataField, createPlainDateField, createSecondLevelField, createStringDataField
} from '../../../../util/format';
import { useFilteredSortedData } from '../../../../util/selector';
import { asDate } from "../../../../util/date";
import ExportGridPopver from '../../ExportPopover/ExportPopover';
import { Link } from 'react-router-dom';

const DEFAULT_SORT : SortOptions = [{ id: 'revcode', direction: 'asc' }];

const { usePageStorePtr } = pageStore('claim-drill-landing');

function isUnmatchedRow( row ) {
    return row.id === 'Unmatched';
}

const renderUnmatchedRowEmpty =  ( { row, value } ) =>
    typeof value === "number" ? (
        <Box component="span" sx={{ display: "block", minWidth: "max-content" }}>
            {`$ ${createFloatingPoint(value)}`}{" "}
        </Box>
    ) : (
        isUnmatchedRow( row ) ? "" : " "
    );

const renderEmptyCell = ({row,value})=> {
    const claimAdjustment=value?.toLowerCase() === "claim adjustment" ? value:null
    
    if (
        claimAdjustment ||
        (!value &&
            row?.rule?.name === null &&
            row?.actualPay > 0 &&
            (!row?.revcode || row?.revcode === null) &&
            (!row?.pxcode || row?.pxcode === null)
        )
    ) {
        return <Link
                to=""
                onClick={(e) => e.preventDefault()}
                style={{ textDecoration: "none",color:'#2739c1', cursor:'text' }}
            >
                {claimAdjustment || 'Claim Level Payment'}
            </Link>;
    }
    return renderProcessTypeHoverLink( row => {
                
               let feeScheduleItems:any= [
                {
                    label: "Name",
                    data: row?.rule?.name || ''
                }];
                const ruleInfo = [
                    ...(row?.rule?.useSplitPayment
                        ? [{label: "Split Payment",data: "Yes" }]
                        : [
                              {
                                  label: "Pay Method",
                                  data: row?.rule?.payMethod?.name || "",
                              },
                              {
                                  label: "Pay Value",
                                  data: row?.rule?.payValue || "",
                              },
                          ]),
                ];
      
                if( !isUnmatchedRow(row) ){
                    feeScheduleItems = [
                        ...feeScheduleItems,
                        {
                            label: "Type",
                            data: row?.rule?.ruleType?.name || ''
                        },...ruleInfo,{
                            type: "LINK",
                            label: "Go to Rule",
                            data: `/cms/contract/${row?.contractid}/rule/${row?.contractServiceId}/edit/${row?.ruleid}/root/true`,
                        }
                    ]
                }

                return feeScheduleItems;
            })( { row, value } );
}
function ClaimLineItemSection({ refreshLineItems, intl }) {
    const dispatch = useDispatch();

    const settings = useGlobalStore((state) => state.settings.data);
    const showRemitLines = settings?.find( x => x.name === 'Show Remit Lines' )?.value?.toLowerCase() === 'true';
    
    const fields = useMemo(() => [
        createSecondLevelField('rule', 'Rule Name', 'name', { 
            onHover: false,
            renderCell: renderEmptyCell,
        }),
        createStringDataField('revcode', 'RevCode', { 
            sortable: true, 
            onHover: true,
            renderCell : renderHoverLink( row => [{
                    label: "",
                    data: row.revcodeDescription || ''
                }]
            )
        }),
        createStringDataField('pxcode', 'PxCode', { 
            sortable: true, 
            onHover: true,
            renderCell : renderHoverLink( row => {
                let pxCodeColumns: HoverColumn[] = [
                    {
                        label: "",
                        data: row.pxcodeDescription || ''
                    }
                ];
                if ((row?.feeScheduleId || row?.analysisGlobalFeeScheduleId) && row.pxcode) {
                    if (row.pxcode.toString().includes(':')) {
                        const splitedPxCode = row.pxcode?.split(":");
                        pxCodeColumns.push({
                            type: "LINK",
                            label: `Go to ${row?.analysisGlobalFeeScheduleId ? 'Global ':''}Fee Schedule Item`,
                            data: `/cms/${row?.analysisGlobalFeeScheduleId ? 'global-' : ''}feeschedules/view/${row.feeScheduleId || row?.analysisGlobalFeeScheduleId}/${splitedPxCode[0]}${(row?.analysisGlobalFeeScheduleId && row?.analysisGlobalFeeScheduleRateId) ? `?rateId=${row?.analysisGlobalFeeScheduleRateId}`:''}`,
                        })
                    } else {
                        pxCodeColumns.push({
                            type: "LINK",
                            label: `Go to ${row?.analysisGlobalFeeScheduleId ? 'Global ':''}Fee Schedule Item`,
                            data: `/cms/${row?.analysisGlobalFeeScheduleId ? 'global-' : ''}feeschedules/view/${row.feeScheduleId || row?.analysisGlobalFeeScheduleId}/${row.pxcode}${(row?.analysisGlobalFeeScheduleId && row?.analysisGlobalFeeScheduleRateId) ? `?rateId=${row?.analysisGlobalFeeScheduleRateId}`:''}`,
                        })
                    }
                }

                if( row.remit ){
                    pxCodeColumns.push({
                        label: "PxCode",
                        data: row.remit.pxCode
                    });
                    pxCodeColumns.push({
                        label: "Paid PxCode",
                        data: row.remit.paidPxCode
                    });
                }
                
                return pxCodeColumns;
            })
         }),
        createNumberDataField('units', 'Qty/Units', { 
            sortable: true,
            valueFormatter: (value, row ) => 
                isUnmatchedRow(row) ? (
                    value !== undefined ? value.toLocaleString() : ''
                ) : ( 
                    value || ' ' 
                ),

        }),
        createPlainDateField('serviceDate', 'Service Date', { 
            sortable: true, 
            whiteSpace: 'pre' ,
            width:100,

        }),
        createCurrencyDataField('chargedAmount', 'Charged Amount', { 
            sortable: true, 
            calculateTotal: true, 
            width:70,

            totals: true,
            renderCell: renderUnmatchedRowEmpty,
        }),
        createCurrencyDataField('expectedPay', 'Expected Pay', { 
            sortable: true, 
            calculateTotal: true, 
            totals: true,
            renderCell: ({ row, value }) => {
                // Show N/A only when value is null (not 0)
                if (value === null || value === undefined) {
                    return 'N/A';
                }
                // Use the standard unmatched row rendering for other cases
                return renderUnmatchedRowEmpty({ row, value });
            }
        }),
        createCurrencyDataField('actualPay', 'Allowable', { sortable: true, calculateTotal: true, totals: true }),
        createCurrencyDataField('variance', 'Variance', { sortable: true, calculateTotal: true, totals: true }),
    ], []);

    const sortPtr = usePageStorePtr('sort', DEFAULT_SORT),
        sort = sortPtr.value;

    const claimAnalysis = useGlobalStore((state) => state.claimAnalysis.detail);

    const filteredData = useFilteredSortedData( fields.map( x => x.id ), "", sort, claimAnalysis);

    const remits = useGlobalStore((state) => state.claimAnalysis.remits);

    const enableRemitsPtr = useStatePtr(true),
        enableRemits = enableRemitsPtr.value && showRemitLines;

    const [ rows, remitRows ] = useRemitData( filteredData, remits, enableRemits );

    const claimDetails = useGlobalStore((state) => state.claimAnalysis.claimAllDetails);
    const claimnumber = claimDetails?.claimnumber || null;
    const claimLineItemPending = useGlobalStore((state) => state.claimAnalysis.get.pending);

    useEffect(() => {
        return () => {
            dispatch(resetClaimLineItem());
        }
    }, [dispatch])

    const handleExportClick = (downloadSummary=true,callback=undefined) => {
        if (callback) {
            callback();
        }
        let csvFields = fields.map((field) => {
            return {
                label: field.label,
                value: field.id === 'rule' ? 'rule.name' : field.id,
            };
        });
        csvFields = [
            ...csvFields,
            {
                label: 'DRG',
                value: 'drg',
            },
        ];
        const json2csvParser = new Parser({ fields: csvFields });
    const flattenedRows = [];

    (enableRemitsPtr.value ? rows : filteredData)?.forEach((row, index) => {
        const claimAdjustment = row?.claimAdjustment;
        const value = row?.value;
        let ruleName = row?.rule?.name;

        if (
            claimAdjustment ||
            (!value &&
                row?.rule?.name === null &&
                row?.actualPay > 0 &&
                (!row?.revcode || row?.revcode === null))
        ) {
            ruleName = 'Claim Level Payment';
        }

        const mainRow = {
            ...row,
            rule: { ...row?.rule, name: ruleName },
        };

        flattenedRows.push(mainRow);

        const filteredRemitRows:any = remitRows.filter((x) => x?.group === row?.id);

        const numSubrows = filteredRemitRows?.length;

        const emptyRow = {revcode: " ", pxcode: " ", serviceDate: " ", units: " ", chargedAmount: " ", actualPay: " ",};
        const keys = ["revcode", "pxcode", "serviceDate", "units", "chargedAmount", "actualPay"];
        const headerValues = ["Rev Code", "Px Code", "Service Date", "Units", "Charged Amount", "Allowable"];

        if (enableRemits && !downloadSummary) {
            flattenedRows.push(emptyRow); //empty row for better indentation

            if (numSubrows > 0) {
                flattenedRows.push(
                    Object.fromEntries([
                        ["rule", { name: "" }], //empty row from left for better indentation
                        ...keys.map((k, i) => [k, headerValues[i]]),
                    ])
                );
            }

            filteredRemitRows.forEach((subrow) => {
                flattenedRows.push(
                    Object.fromEntries([...keys.map((k) => [k, subrow[k]])])
                );
            });

            flattenedRows.push(emptyRow); //empty row for better indentation
        }

    });

        const csv = json2csvParser.parse(flattenedRows);

        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${claimnumber}_claimDetails.csv`;
        a.click();
    };

    const tableLevelAction = () => {
        return (
            <>
                <Stack direction="row" spacing={1} alignItems="end">
                    { showRemitLines ?
                        <Stack direction="row" alignItems="center" paddingBottom="3px">
                            <Switch checkedPtr={enableRemitsPtr} size='small' /> Show matched service lines
                        </Stack>
                    : null }

                    {!enableRemits && (
                        <Tooltip title="Export">
                            <IconButton
                                aria-label="export"
                                size="small"
                                onClick={() => handleExportClick(true)}
                            >
                                <GetAppIcon />
                            </IconButton>
                        </Tooltip>
                    )}

                    {enableRemits && (
                        <ExportGridPopver
                            customDownload={handleExportClick}
                            customTooltip="Export"
                            customOptions={{
                                option1: "Export Summary",
                                option2: "Export Details",
                            }}
                        />
                    )}
                </Stack>
            </>
        );
    }

    const handleViewServiceAdjustment = (rowData) => {
        dispatch(setAdjustment({
            open: true,
            type: 'SERVICE',
            id: rowData?.servicepaymentid
        }));
    }

    const claimActions = ({ row }) => {

        return row.id === "Unmatched"
            ? []
            : [
                  <GridActionsCellItem
                      onClick={() => handleViewServiceAdjustment(row)}
                      label="View Service Adjustments"
                      icon={
                          <Tooltip title="View Service Adjustments">
                              <Typography
                                  sx={{
                                      cursor: "pointer",
                                      color: "blue",
                                      textDecoration: "underline",
                                      fontSize: "12px",
                                  }}
                              >
                                  {" "}
                                  {row?.serviceAdjustmentCodes?.map(
                                      (item, index) =>
                                          `${index !== 0 ? " / " : ""}` + item
                                  )}
                              </Typography>
                          </Tooltip>
                      }
                  />,

                  <div>
                      {row?.isDenial && (
                           <Box className={`fas fa-times-circle text-danger ml-2 null font-14`} />
                      )}
                  </div>,
              ];
    };

    function actionLevelGrid() {
        return (
            <Box component="h6" marginBottom={0}>
                <Chip size='small' label={ filteredData?.length ?? 0 } /> claim charge line(s)
            </Box>
        )
    }

    const getDetailPanelContent = React.useCallback(
        ({ row }: GridRowParams) => {
            // Check if this row has claim level payment
            const claimAdjustment = row?.rule?.name?.toLowerCase() === "claim adjustment";
            const isClaimLevelPayment = 
                claimAdjustment ||
                (!row?.rule?.name &&
                    row?.actualPay > 0 &&
                    (!row?.revcode || row?.revcode === null) &&
                    (!row?.pxcode || row?.pxcode === null));
            
            // Don't show detail panel (plus icon) for claim level payments
            if (isClaimLevelPayment) {
                return null;
            }
            
            return remits.length ?
                <ServiceLines 
                    row={row} 
                    remits={
                        remitRows.filter( x => x.group === row.id )
                    } 
                />
            : null;
        }
    , [ remitRows, remits.length ]);

    return (
        <Stack direction="column" spacing={1} sx={{ height: '100%' }}>
            <TableLevelAction 
                actions={tableLevelAction}
                actionLevelGrid={actionLevelGrid}
                handleRefresh={() => {
                    refreshLineItems();
                }}
            />
            <XClientDataGrid
                key={ enableRemits ? 'remits' : 'no-remits' }
                sx={{
                    '& .matched-remit-row': {
                        backgroundColor: '#FAFFFA',
                    },
                    '& .unmatched-remit-row': {
                        backgroundColor: '#FFFAFA',
                    },
                    '& .unmatched-remit-group': {
                        fontWeight: 600,
                    },
                }}
                fields={fields}
                data={rows}
                displayTotal={true}
                loading={claimLineItemPending}
                sortPtr={ sortPtr }
                actions={claimActions}
                actionColumnName='Adjustments'
                hideFooter

                getDetailPanelContent={ enableRemits ? 
                    getDetailPanelContent 
                : null }

                getDetailPanelHeight={ enableRemits ? 
                    ({ row }) => 'auto' 
                : null } // Height based on the content.
            />
        </Stack>
    );
}

export default injectIntl(ClaimLineItemSection);

function useRemitData( filteredData : any[], remits : ClaimPaymentDetails[], enableRemits : boolean ) {
    return useMemo(() => {
        if( !enableRemits ){
            return [ filteredData, [] ];
        }

        const topLevel = filteredData.map( row => ({ ...row, group : null })),
            claimChargeIds = filteredData.map( row => row.claimchargeid );

        const dedupRemits = Array.from( new Map( remits.map( item => [ item.servicePaymentId, item ] ) ).values() );

        const remitRows = dedupRemits
            .map( row => ({ 
                id: row.servicePaymentId,
                pxcode : row.paidPxCode || row.pxCode,
                revcode: row.revCode || row.revCode,
                units: row.units,
                serviceDate: row.serviceDate,
                chargedAmount: row.chargedAmount,
                actualPay: row.actualPay,
                rule: { name : '' },
                
                group : claimChargeIds.includes( row.claimChargeId ) ?
                    filteredData.find( x => x.claimchargeid === row.claimChargeId )?.id :
                    'Unmatched',
                remit: row,
            }));

        const unmatchedTotal = remitRows.reduce( ( acc, row ) => {
            if( row.group === 'Unmatched' ){
                acc.actualPay += row.actualPay;
                acc.count++;
            }
            return acc;
        }, { actualPay: 0, count : 0 });

        const unmatched =unmatchedTotal.count ?
            [{
                id: 'Unmatched',
                pxcode : '',
                revcode: '',
                units: undefined,
                chargedAmount: undefined,
                actualPay: unmatchedTotal.actualPay,
                variance: -unmatchedTotal.actualPay,
                rule: { name : 'Unmatched service lines' },
                group : null,
                remit: null
            }] :
            [];

        return [[...topLevel, ...unmatched ], remitRows];
    }, [filteredData, remits, enableRemits]);
}

const ServiceLines = ({ row, remits }) =>
    <TableContainer key={`tablecontainer-${row.id}`} sx={{
        flex: 1, ml: '50px', mr: "auto", width: '90%', maxWidth: 800, p: 1,
        marginBottom: 1,
    }}>
        { row.id !== 'Unmatched' ?
            <Box component="h6" sx={{ color: "#646c9a"}}>
             Matched service lines
            </Box>
        : null }

        <Table key={`table-${row.id}`} size="small">
            <TableHead>
                <TableRow key={`childrow-${row.id}`} sx={{
                    '& th': {
                        color: '#848484',
                        padding: '0px 16px',
                        fontWeight: 400,                            
                    }
                }}>
                    <TableCell>Rev Code</TableCell>
                    <TableCell>Px Code</TableCell>
                    <TableCell>Service Date</TableCell>
                    <TableCell align="right">Units</TableCell>
                    <TableCell align="right">Charged Amount</TableCell>
                    <TableCell align="right">Allowable</TableCell>
                </TableRow>
            </TableHead>
            <TableBody sx={{
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            }}>
                { remits.map( (remit, index) =>
                    <TableRow key={`childrow-${row.id}-${index}`}>
                        <TableCell>{ remit.revcode }</TableCell>
                        <TableCell>{ remit.pxcode }</TableCell>
                        <TableCell>{ asDate(remit.serviceDate) ?? 'N/A' }</TableCell>
                        <TableCell align="right">{ remit.units }</TableCell>
                        <TableCell align="right">{ asDollars( remit.chargedAmount ) }</TableCell>
                        <TableCell align="right">{ asDollars( remit.actualPay ) }</TableCell>
                    </TableRow>
                )}
            </TableBody>
        </Table>
    </TableContainer>